/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Shared template for individual feature pages (/features/*).
 * Linear-inspired structure: Hero → Feature Grid → Deep-Dive Sections → Connections → CTA
 *
 * Screenshot slots: when `screenshotPath` is provided on a workflow step or capability,
 * it renders the image. When absent, it renders the text-only version.
 * This lets us progressively add screenshots without changing page configs.
 */

"use client";

import { CheckCircle, ArrowRight } from "lucide-react";
import Link from "next/link";
import MainNavigation from "@/components/layout/main-navigation";
import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { CTA, TRUST } from "@/content/landing/constants";
import { FadeIn } from "@/components/landing/primitives/fade-in";
import { getCapabilitiesSectionBgClass } from "./get-capabilities-section-bg-class";
import { ScreenshotSlot } from "./screenshot-slot";
import type { FeaturePageConfig } from "./types";

export function FeaturePageTemplate({ config }: { config: FeaturePageConfig }) {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      {/* Hero */}
      <MarketingPageHero
        variant="dark"
        badge={config.badge}
        title={
          <>
            {config.title}{" "}
            <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
              {config.titleHighlight}
            </span>
          </>
        }
        description={config.description}
      />

      {/* Hero Screenshot — full-width product UI below hero */}
      {config.heroScreenshotPath && (
        <section className="pb-16 md:pb-24 bg-primary -mt-8 md:-mt-16">
          <div className="max-w-[1100px] mx-auto px-4 md:px-8">
            <ScreenshotSlot
              path={config.heroScreenshotPath}
              alt={config.heroScreenshotAlt}
            />
          </div>
        </section>
      )}

      {/* Feature Grid — bento overview of sub-features */}
      {config.featureGrid && (
        <section className="py-16 md:py-24 bg-background">
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
            <FadeIn>
              <h2 className="text-[clamp(24px,3.5vw,36px)] font-bold text-foreground mb-10 font-display text-center">
                {config.featureGrid.title}
              </h2>
            </FadeIn>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {config.featureGrid.items.map((item, index) => {
                const Icon = item.icon;
                return (
                  <FadeIn
                    key={item.title}
                    delay={index * 100}
                    className="rounded-2xl border border-border/60 p-6 bg-muted/30 hover:border-secondary/30 hover:shadow-md transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-foreground mb-1.5 font-display">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Workflow Story — each step is its own full-width section */}
      {config.workflow && (
        <>
          {/* Workflow header */}
          <section
            className="py-16 md:py-20 bg-background"
            aria-label="How it works"
          >
            <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20 text-center">
              <FadeIn>
                <h2 className="text-[clamp(28px,4vw,44px)] font-bold text-foreground mb-3 font-display">
                  {config.workflow.title}
                </h2>
              </FadeIn>
              <FadeIn delay={100}>
                <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                  {config.workflow.description}
                </p>
              </FadeIn>
            </div>
          </section>

          {/* Workflow steps — alternating backgrounds */}
          <div>
            {config.workflow.steps.map((step, index) => {
              const isEven = index % 2 === 0;

              return (
                <FadeIn
                  key={step.label}
                  delay={index * 100}
                  className={`py-12 md:py-20 ${
                    index % 2 === 0 ? "bg-background-warm" : "bg-background"
                  } ${index === 0 ? "" : "border-t border-border/30"}`}
                >
                  <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
                    <div
                      className={`flex flex-col gap-8 md:gap-12 items-start ${
                        step.screenshotPath
                          ? isEven
                            ? "lg:flex-row"
                            : "lg:flex-row-reverse"
                          : ""
                      }`}
                    >
                      {/* Text content */}
                      <div
                        className={`${step.screenshotPath ? "lg:w-1/2" : "max-w-2xl"}`}
                      >
                        {/* Step number */}
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center mb-4">
                          <span className="text-sm font-bold text-white">
                            {index + 1}
                          </span>
                        </div>

                        {/* Label + title */}
                        <span className="text-xs font-semibold text-secondary uppercase tracking-wider">
                          {step.label}
                        </span>
                        <h3 className="text-2xl font-bold text-foreground mb-3 mt-1 font-display">
                          {step.title}
                        </h3>

                        <p className="text-base text-muted-foreground leading-relaxed mb-5">
                          {step.description}
                        </p>

                        <ul className="space-y-2.5">
                          {step.details.map((detail) => (
                            <li
                              key={detail}
                              className="flex items-start gap-2.5 text-sm text-muted-foreground"
                            >
                              <CheckCircle
                                className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5"
                                aria-hidden="true"
                              />
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Screenshot slot */}
                      {step.screenshotPath && (
                        <div className="lg:w-1/2">
                          <ScreenshotSlot
                            path={step.screenshotPath}
                            alt={step.screenshotAlt || step.title}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </>
      )}

      {/* Capabilities — 2x2 card grid matching solution template pattern */}
      <section
        className={`py-16 md:py-24 ${getCapabilitiesSectionBgClass(config)}`}
        aria-label="Capabilities"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <div className="grid md:grid-cols-2 gap-6">
            {config.capabilities.map((cap, index) => {
              const Icon = cap.icon;

              return (
                <FadeIn
                  key={cap.title}
                  as="article"
                  delay={index * 100}
                  className="card-premium rounded-2xl p-6 md:p-8 border border-border"
                >
                  {/* Icon + title inline */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground font-display">
                      {cap.title}
                    </h3>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {cap.description}
                  </p>

                  <ul className="space-y-2" role="list">
                    {cap.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex items-start gap-2 text-sm"
                      >
                        <CheckCircle
                          className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <span className="text-muted-foreground">{bullet}</span>
                      </li>
                    ))}
                  </ul>

                  {cap.screenshotPath && (
                    <div className="mt-5">
                      <ScreenshotSlot
                        path={cap.screenshotPath}
                        alt={cap.screenshotAlt || cap.title}
                      />
                    </div>
                  )}
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Connects */}
      {config.connections && (
        <section
          className="py-16 md:py-24 bg-background-warm"
          aria-label="How it connects"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
            <FadeIn>
              <h2 className="text-[clamp(24px,3.5vw,36px)] font-bold text-foreground mb-3 font-display text-center">
                {config.connections.title}
              </h2>
            </FadeIn>
            <FadeIn delay={100}>
              <p className="text-base text-muted-foreground text-center max-w-2xl mx-auto mb-10">
                Data flows through the platform. Here&apos;s where this feature
                fits.
              </p>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {config.connections.items.map((conn, index) => {
                const Icon = conn.icon;
                return (
                  <FadeIn
                    key={conn.flow}
                    delay={index * 100}
                    className="card-premium p-6 rounded-2xl border border-border"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-bold text-foreground font-display">
                        {conn.flow}
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {conn.description}
                    </p>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Deep Dives — child pages of this feature */}
      {config.deepDives && config.deepDives.items.length > 0 && (
        <section
          className="py-16 md:py-24 bg-background-warm"
          aria-label="Dive deeper"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
            <FadeIn>
              <header className="mb-8 md:mb-10 max-w-3xl">
                <span className="section-badge mb-4">Dive Deeper</span>
                <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground mb-3 font-display">
                  {config.deepDives.title}
                </h2>
                {config.deepDives.description && (
                  <p className="text-[clamp(14px,1.5vw,16px)] leading-[1.5] text-muted-foreground">
                    {config.deepDives.description}
                  </p>
                )}
              </header>
            </FadeIn>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {config.deepDives.items.map((dive, index) => {
                const Icon = dive.icon;
                return (
                  <FadeIn key={dive.href} delay={index * 80}>
                    <Link
                      href={dive.href}
                      className="group flex h-full flex-col rounded-2xl border border-border bg-background p-6 hover:border-secondary/40 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-secondary" />
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground/0 group-hover:text-secondary transition-colors ml-auto" />
                      </div>
                      <h3 className="text-base font-semibold text-foreground mb-1 group-hover:text-secondary transition-colors font-display">
                        {dive.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {dive.description}
                      </p>
                    </Link>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Related Features */}
      {config.relatedFeatures && config.relatedFeatures.length > 0 && (
        <section
          className="py-16 md:py-24 bg-background"
          aria-label="Related features"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
            <FadeIn>
              <h2 className="text-xl font-bold text-foreground mb-6 font-display">
                Related features
              </h2>
            </FadeIn>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {config.relatedFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <FadeIn key={feature.href} delay={index * 100}>
                    <Link
                      href={feature.href}
                      className="group flex items-center gap-3 rounded-xl border border-border bg-muted/20 p-4 hover:border-secondary/30 hover:shadow-md transition-all"
                    >
                      <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground group-hover:text-secondary transition-colors">
                        {feature.label}
                      </span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground/0 group-hover:text-secondary transition-colors ml-auto" />
                    </Link>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <MarketingCTASection
        title="See it for yourself"
        description={`${TRUST.STRIP[0]}. No credit card required. Pick your trade and start estimating.`}
        primaryCTA={{ text: CTA.PRIMARY, href: CTA.PRIMARY_HREF }}
        secondaryCTA={{
          text: CTA.SECONDARY_CONTACT,
          href: CTA.CONTACT_HREF,
        }}
        trustIndicators={[TRUST.INLINE]}
      />

      <Footer />
    </div>
  );
}
